import { useEffect, useState } from "react";
import { formatDate } from "@/lib/utils";

export interface ShareReportDialogProps {
  open: boolean;
  onClose: () => void;
  token: string | null;
  errorMessage?: string | null;
  expiresAt?: string | number | null;
  isPending?: boolean;
}

export default function ShareReportDialog({
  open,
  onClose,
  token,
  errorMessage,
  expiresAt,
  isPending,
}: ShareReportDialogProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const url = token ? `${window.location.origin}/r/${token}` : "";

  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      const input = document.getElementById("share-report-url") as HTMLInputElement | null;
      input?.select();
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="share-report-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg border bg-background p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="share-report-title" className="text-lg font-semibold">
          Share this dashboard
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Anyone with this link can view a read-only snapshot of this company.
          {expiresAt ? ` Expires ${formatDate(expiresAt)}.` : ""}
        </p>

        <div className="mt-5">
          {isPending ? (
            <div className="rounded-md border bg-secondary/50 px-3 py-2 text-sm text-muted-foreground">
              Generating snapshot…
            </div>
          ) : errorMessage ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {errorMessage}
            </div>
          ) : token ? (
            <div className="flex gap-2">
              <input
                id="share-report-url"
                type="text"
                readOnly
                value={url}
                onFocus={(e) => e.currentTarget.select()}
                className="flex-1 rounded-md border bg-background px-3 py-2 text-sm"
              />
              <button
                type="button"
                onClick={copy}
                className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
              >
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
          ) : null}

          {token ? (
            <div className="mt-3 flex justify-between text-sm">
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
              >
                Open in new tab →
              </a>
              <button
                type="button"
                onClick={onClose}
                className="text-muted-foreground hover:text-foreground"
              >
                Done
              </button>
            </div>
          ) : (
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border px-3 py-2 text-sm hover:bg-secondary"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
